import { useMemo } from 'react';
import styled from 'styled-components';
import { Trans } from 'next-i18next';

import { getStaticCDN } from '@design-system/utils/utils';
import useWindowResize from '@source/hooks/useWindowResize';
import StyledContainer from '@design-system/styled/Container';

const StyledWrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #ffede5;
  text-align: center;
  padding-top: 40px;
  ${(props) => props.theme.typo.familyGoogle.regular}

  .title-group {
    display: inline-block;
    text-align: center;
  }

  .section-subtitle {
    display: inline-block;
    margin: 16px auto;
    background-color: #ff4d00; /* Bright orange-red */
    color: white;
    border: none;
    padding: 10px 12px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 10px;
    line-height: 1;
  }

  /* Responsive: shorten line for small screens */
  @media (max-width: 576px) {
    .section-subtitle::before,
    .section-subtitle::after {
      flex: unset;
      width: 44px;
    }
  }

  h2 {
    font-size: 55px;
    margin: 8px 0 16px 0;
    line-height: 45px;
    font-weight: 300;
    @media (max-width: 992px) {
      font-size: 50px;
    }

    @media (max-width: 768px) {
      font-size: 45px;
    }

    @media (max-width: 576px) {
      font-size: 40px;
    }
  }

  p {
    margin: 0;
    font-size: 20px;
    line-height: 25px;

    @media (max-width: 992px) {
      font-size: 25px;
    }

    @media (max-width: 768px) {
      font-size: 20px;
    }

    @media (max-width: 576px) {
      font-size: 18px;
    }
  }

  p.in-red {
    color: #ff4c00;
  }

  .sell-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    font-weight: 500;
    letter-spacing: 3px;
    line-height: 100%;
    padding: 16px 0px;

    @media (max-width: 375px) {
      line-height: 20px;
    }
  }

  .sell-button::before,
  .sell-button::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #ff4c00;
    margin-top: 1px;
  }

  .sell-button > button {
    background-color: transparent;
    color: #ff4d00; /* Bright orange-red */
    border: none;
    font-size: 20px;
    font-weight: 500;
    border-radius: 10px;
    cursor: pointer;
    transition: color 0.3s ease, transform 0.2s ease;
    line-height: 1;
  }

  figure {
    margin-bottom: 0;
    @media (max-width: 992px) {
      margin-top: -32px;
    }
  }
`;

type MascotImgKey = 'img1920' | 'img1280' | 'img992' | 'img768' | 'img576' | 'img375';

const mascotImg: Record<MascotImgKey, string> = {
  img1920: getStaticCDN('/assets/shared/sell-car/mascot_1920.svg'),
  img1280: getStaticCDN('/assets/shared/sell-car/mascot_1920.svg'),
  img992: getStaticCDN('/assets/shared/sell-car/mascot_992.svg'),
  img768: getStaticCDN('/assets/shared/sell-car/mascot_768.svg'),
  img576: getStaticCDN('/assets/shared/sell-car/mascot_576.svg'),
  img375: getStaticCDN('/assets/shared/sell-car/mascot_375.svg'),
};

const InspectionFeeWaive = () => {
  const [width] = useWindowResize();

  const keyPath = useMemo(() => {
    let key = null;
    const supportedScreenSizes = [375, 576, 768, 992, 1280, 1920];
    for (let index = 0; index < supportedScreenSizes.length; index += 1) {
      const ele = supportedScreenSizes[index];
      if (!width) key = 'img375';
      if (width <= supportedScreenSizes[0]) {
        key = 'img375';
        break;
      }
      if (width >= supportedScreenSizes[supportedScreenSizes.length - 1]) {
        key = 'img1920';
        break;
      }
      const nextEle = supportedScreenSizes[index + 1];
      if (width >= ele && width < nextEle) {
        key = `img${ele}`;
      }
    }

    return key;
  }, [width]);

  let screen = 'desktop';
  if (width && width <= 375) {
    screen = 'smallMobile';
  } else if (width && width <= 576) {
    screen = 'mobile';
  } else if (width && width <= 992) {
    screen = 'tablet';
  }

  const imageSrc = useMemo(() => mascotImg[keyPath as MascotImgKey], [keyPath]);

  const handleScroll = () => {
    const target = document.getElementById('request-form-wrapper');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <StyledWrapper className="inspection-fee-waive">
      <StyledContainer>
        <div className="title-group">
          <div className="section-subtitle">
            <Trans ns="sellCar" i18nKey={`sellCar.inspectionFeeWaive.${screen}.sub_title`} />
          </div>
          <h2 className="main-title">
            <Trans
              ns="sellCar"
              i18nKey={`sellCar.inspectionFeeWaive.${screen}.main_title`}
              components={{ br: <br /> }}
            />
          </h2>
        </div>
        <p className="in-red">
          <Trans
            ns="sellCar"
            i18nKey={`sellCar.inspectionFeeWaive.${screen}.description_highlight`}
            components={{ br: <br /> }}
          />
        </p>
        <p>
          <Trans
            ns="sellCar"
            i18nKey={`sellCar.inspectionFeeWaive.${screen}.description`}
            components={{ br: <br /> }}
          />
        </p>
        <div className="sell-button">
          <button onClick={handleScroll}>
            <Trans ns="sellCar" i18nKey={`sellCar.inspectionFeeWaive.sellButton`} />
          </button>
        </div>
        <figure className="image-wrapper">
          <img src={imageSrc} alt="mascot" class="mascot" />
        </figure>
      </StyledContainer>
    </StyledWrapper>
  );
};

export default InspectionFeeWaive;
